/** @file SettingNumber: a settings number field: debounced, clamped and written back, empty ignored. */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { settingNumberValue } from './settingNumberValue';

/** `value` stored now, `onCommit` called with each applied value; `min`/`max` clamp, `integer` rounds, `debounceMs` defaults to 300. */
export type SettingNumberProps = {
  value: number; onCommit: (value: number) => void; label: string; min?: number; max?: number; integer?: boolean; step?: number; debounceMs?: number;
};

/**
 * Typing starts a timer; when it fires (or on Enter, blur and unmount: the flush) the text is read once: an empty
 * field is ignored, a number is rounded and clamped, committed when it differs, and a clamped value replaces the text so
 * the field never shows a number that was not stored. A field left empty shows the stored value again on blur.
 */
export function SettingNumber({ value, onCommit, label, min, max, integer, step = 1, debounceMs = 300 }: SettingNumberProps): ReactElement {
  const [text, setText] = useState(String(value));
  const typed = useRef(text);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ value, onCommit, range: { min, max, integer } });
  latest.current = { value, onCommit, range: { min, max, integer } };
  const show = (next: string): void => { typed.current = next; setText(next); };
  const flush = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const r = settingNumberValue(typed.current, latest.current.range);
    if (r.kind === 'ignore') return;
    if (r.value !== latest.current.value) latest.current.onCommit(r.value);
    if (r.changed) show(String(r.value));
  };
  useEffect(() => { show(String(value)); }, [value]);
  useEffect(() => () => { if (timer.current !== null) flush(); }, []);
  return (
    <input className="p3-input p3-number" type="number" aria-label={label} step={step} min={min} max={max} value={text}
      onChange={(e) => {
        show(e.target.value);
        if (timer.current !== null) clearTimeout(timer.current);
        timer.current = setTimeout(flush, debounceMs);
      }}
      onKeyDown={(e) => { if (e.code === 'Enter' || e.code === 'NumpadEnter') flush(); }}
      onBlur={() => { flush(); if (settingNumberValue(typed.current).kind === 'ignore') show(String(latest.current.value)); }} />
  );
}
